import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as ActionCable from 'actioncable';

/**
 * Broadcaster - manages an internal event bus for ActionCable channel messages.
 * Replaces the `Broadcaster` class from the abandoned `ngx-cable` package.
 */
@Injectable()
export class Broadcaster {
  private _eventBus: Subject<{ key: string; data: any }>;

  constructor() {
    this.init();
  }

  private init(): void {
    this._eventBus = new Subject<{ key: string; data: any }>();
  }

  public broadcast(key: string, data: any): void {
    this._eventBus.next({ key, data });
  }

  public on(key: string): Observable<any> {
    return this._eventBus.asObservable().pipe(
      filter((event) => event.key === key),
      map((event) => event.data)
    );
  }

  public off(): void {
    this.init();
  }
}

/**
 * NgXCable - wraps the `actioncable` npm package for Angular.
 * Replaces the `NgXCable` class from the abandoned `ngx-cable` package.
 *
 * Provides the same public API:
 *   connect(url), create(params), subscribe(params), send(data),
 *   perform(action, data), unsubscribe(), getSubscriptions(),
 *   getCountSubscriptions(), searchSubcriptions(id, field),
 *   reject(subscription), disconnect(), isOpen()
 */
@Injectable()
export class NgXCable {
  private cable: any;

  constructor(private broadcaster: Broadcaster) {}

  public setCable(url: string): void {
    this.cable = ActionCable.createConsumer(url);
  }

  public connect(url: string): void {
    this.setCable(url);
  }

  public isOpen(): boolean {
    if (this.cable) {
      return !this.cable.connection.disconnected;
    }
    return false;
  }

  public create(params: { channel: string; room: string }): any {
    return this.cable.subscriptions.create(params, {
      received: (data: any) => {
        this.broadcaster.broadcast(params.channel, data);
      }
    });
  }

  public subscribe(params: { channel: string; room: string }): any {
    return this.create(params);
  }

  public send(data: any, subscriptions?: any[]): boolean {
    if (!this.isOpen()) {
      return false;
    }

    if (subscriptions == null) {
      this.cable.subscriptions.subscriptions[0].send(data);
    } else if (subscriptions instanceof Array) {
      subscriptions.forEach((subscription) => {
        subscription.send(data);
      });
    } else {
      return false;
    }

    return true;
  }

  public perform(action: string, data: any, subscriptions?: any[]): boolean {
    if (!this.isOpen()) {
      return false;
    }

    if (subscriptions == null) {
      this.cable.subscriptions.subscriptions[0].perform(action, data);
    } else if (subscriptions instanceof Array) {
      subscriptions.forEach((subscription) => {
        subscription.perform(action, data);
      });
    } else {
      return false;
    }

    return true;
  }

  public unsubscribe(subscriptions?: any[]): boolean {
    if (subscriptions == null) {
      const subs = [...this.cable.subscriptions.subscriptions];
      subs.forEach((subscription) => {
        this.cable.subscriptions.remove(subscription);
      });
    } else if (subscriptions instanceof Array) {
      subscriptions.forEach((subscription) => {
        this.cable.subscriptions.remove(subscription);
      });
    } else {
      return false;
    }

    return true;
  }

  public getSubscriptions(): any[] {
    return this.cable.subscriptions.subscriptions;
  }

  public getCountSubscriptions(): number {
    return this.getSubscriptions().length;
  }

  public searchSubcriptions(id: string, field: string = 'room'): any[] {
    const rsub: any[] = [];

    this.getSubscriptions().forEach((subscription) => {
      const msg = JSON.parse(subscription.identifier);
      if (id === msg[field]) {
        rsub.push(subscription);
      }
    });

    return rsub;
  }

  public reject(subscription: any): boolean {
    if (subscription) {
      this.cable.subscriptions.remove(subscription);
      return true;
    }
    return false;
  }

  public disconnect(): boolean {
    if (this.isOpen()) {
      this.cable.disconnect();
      return true;
    }
    return false;
  }
}
